<template>
    <div :class="['intranet-bottom-toolbar', {'has-chat': showChat}, {'has-telephone': showTelephone}]">
        <div v-if="showChat || showTelephone" class="bar flex">
            <app-toolbar-telephone v-if="showTelephone"/>
            <app-chat v-if="showChat"/>
        </div>
        <div class="bar window-hidden-lg-down" v-if="$store.state._debug">
            <template v-if="errors.length">
                <app-popover wrapper-element="span">
                    <template #default="{toggle}">
                        <span v-interact="toggle" class="icon-interact">
                            <i aria-hidden="true" class="material-icons" role="img">error</i>
                        </span>
                    </template>
                    <template #container>
                        <table class="text-left">
                            <tr @click="clearError">
                                <td colspan="3">clear error page</td>
                            </tr>
                            <tr v-for="(error, $index) in errors.slice(-10)" @click="setError($index)">
                                <td>[{{dateHelper('H:i:s', error.time)}}]</td>
                                <td class="text-left">{{error.statusCode}}: {{error.url}}</td>
                                <td>{{error.message.slice(0,30) + (error.message.length > 30 ? ' ...' : '')}}</td>
                            </tr>
                        </table>
                    </template>
                </app-popover>
            </template>
            <template v-if="$store.state._debugHistory.length">
                <app-popover wrapper-element="span">
                    <template #default="{toggle}">
                        <span v-interact="toggle" class="icon-interact">
                            <i aria-hidden="true" class="material-icons" role="img">history</i>
                        </span>
                    </template>
                    <template #container>
                        <table>
                            <tr v-for="history in $store.state._debugHistory.slice(-10)">
                                <td>[{{dateHelper('H:i:s', history.time)}}]</td>
                                <td class="text-left">{{history.url}}</td>
                                <td>{{history.duration.toFixed(3)}}s</td>
                                <td>{{history.memory}}</td>
                                <td>{{history.queries.all}} Queries</td>
                            </tr>
                        </table>
                    </template>
                </app-popover>
            </template>
            <span>Time: {{$store.state._debug.duration && $store.state._debug.duration.toFixed(4)}}</span>
            <span class="window-visible-md-upward">Memory: {{$store.state._debug.memory}}</span>
            <span v-tooltip="debugQueryTitle">Queries: {{$store.state._debug.queries.all}}</span>
            <span>Intranet: {{$store.state._debug.intranetId}} | {{$store.state._debug.hostname}}</span>
            <app-popover wrapper-element="span">
                <template #default="{toggle}">
                    <span v-interact="toggle" class="icon-interact">
                        <i aria-hidden="true" class="material-icons" role="img">more_vert</i>
                    </span>
                </template>
                <template #container>
                    <ul class="portuna-menu" for="portuna-intranet-menu-handler">
                        <li>
                            <router-link to="/application/debug/notification">
                                <i class="material-icons" aria-hidden="true" role="img">feedback</i><span>Notification Events</span>
                            </router-link>
                        </li>
                        <li>
                            <router-link to="/application/debug/cron">
                                <i class="material-icons" aria-hidden="true" role="img">history</i><span>Cronjobs</span>
                            </router-link>
                        </li>
                        <li>
                            <a @click="sendDebug('/application/debug/acl-cache-reset')" href="javascript:void(0);">
                                <i class="material-icons" aria-hidden="true" role="img">clear</i><span>ACL-Reset</span>
                            </a>
                        </li>
                        <li>
                            <a @click="sendDebug('/application/notification/reset-cache')" href="javascript:void(0);">
                                <i class="material-icons" aria-hidden="true" role="img">clear</i><span>Notification-Cache-Reset</span>
                            </a>
                        </li>
                        <li>
                            <router-link to="/application/debug/acl-dump" target="_blank">
                                <i class="material-icons" aria-hidden="true" role="img">list</i><span>ACL-Dump</span>
                            </router-link>
                        </li>
                        <li>
                            <router-link to="/application/debug/recent-mail" target="_blank">
                                <i class="material-icons" aria-hidden="true" role="img">email</i><span>Letzte (unverschickte) E-Mail</span>
                            </router-link>
                        </li>
                        <li @click="clearStorage">
                            <div><i class="material-icons" role="img">clear</i><span>Clear LocalStorage</span></div>
                        </li>
                        <li v-if="devMode">
                            <router-link :to="{name: 'debug-design'}">
                                <i class="material-icons" aria-hidden="true" role="img">view_compact</i><span>Design-Elemente</span>
                            </router-link>
                        </li>
                    </ul>
                </template>
            </app-popover>
        </div>
    </div>
</template>

<script setup lang="ts">
import { usePortuna } from "@/composables/portuna";
import axios, { AxiosError } from "axios";
import { defineAsyncComponentWrapper } from "@/library/utils";
import {inject} from 'vue';
import localStorage from "../library/localStorage";
import TopMessageHandler from "../library/topMessageHandler";
import { errorHandler } from "../utils/portuna";
import dateHelper from "../utils/portuna/dateHelper";
import AppPopover from "./AppPopover.vue";
import AppToolbarTelephone from "./AppToolbarTelephone.vue";
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
const viewPortSize = inject('viewPortSize');
const portuna = usePortuna();
defineExpose(portuna);
const AppChat = defineAsyncComponentWrapper(() => import("./chat/AppChat.vue"));
const store = useStore();
const route = useRoute();
const mdDown = computed(() => {
  return viewPortSize.xs || viewPortSize.sm || viewPortSize.md;
});
const errors = computed<ErrorData[]>(() => store.state.errors);
const setError = (Error: ErrorData | AxiosError | number | null): void =>
  store.commit("setError", Error);
/* [VUEDC_TODO]: Check function dispatch call signature.*/ const clearError = async (
  ...args: unknown[]
): Promise<unknown> => store.dispatch("clearError", args);
const devMode = ref(process.env.NODE_ENV !== "production");
const sendDebug = (url: string) => {
  axios
    .get(url)
    .then((response) => {
      TopMessageHandler.success(response.data.message || "Done.");
    })
    .catch(errorHandler);
};
const clearStorage = () => {
  localStorage.clear();
  window.location.reload();
};
const debugQueryTitle = computed(() => {
  let debugValue = store.state._debug.queries;
  let html = "Select: " + debugValue.select + "<br />";
  html += "Update: " + debugValue.update + "<br />";
  html += "Insert: " + debugValue.insert + "<br />";
  html += "Delete: " + debugValue.delete + "<br />";
  return html;
});
const isMainLayout = computed(() => {
  return route && !["blank", "minimal"].includes(route.meta.layout);
});
const showChat = computed(() => {
  return isMainLayout.value && portuna.currentUser.value.hasChat;
});
const showTelephone = computed(() => {
  return (
    isMainLayout.value && portuna.currentUser.value.id && portuna.currentUser.value.telephoneToolbar
  );
});

</script>